import { useEffect, useState, type ReactElement } from 'react';
import { track, trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { emberApi, type Knock, type QuestionRow } from '../api';
import { QuestionCard } from '../components/QuestionCard';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { knockHeadline } from '../components/knock-letters';
import { notifyKnocksChanged } from '../components/use-knocks';
import './QuestionPage.css';

/**
 * The question page `/q/:questionId` (v4.0 §4.19, 2026-09-20 晚拍板): where a
 * question lives once its letters are answered — a read's home is the
 * Ember page, a question has none of its own, so this is it. The asker
 * returns to read the answers as they come, say once per answer that it
 * mattered, or withdraw the question; an answerer returns to read what
 * others said. Reachable by those two sides only; a 404 to everyone else —
 * there is no list, no neighbour, nothing to browse.
 *
 * The page shown is the look (like the paper): the asker's unseen answers and
 * an answerer's unseen "it mattered" are marked seen on load.
 */
export function QuestionPage({ questionId }: { questionId: string }): ReactElement {
  const [row, setRow] = useState<QuestionRow | null>(null);
  const [failed, setFailed] = useState<false | 'gone' | 'load_failed'>(false);
  useEffect(() => {
    let alive = true;
    setFailed(false);
    emberApi.question(questionId)
      .then((r) => {
        if (!alive) return;
        setRow(r);
        track('ui.clicked', { name: 'question_page_open', state: r.role, match_id: r.match_id });
        if (r.letter === 'answers_arrived' || r.letter === 'answer_mattered') {
          emberApi.markQuestionSeen(questionId).then(() => notifyKnocksChanged()).catch(() => { /* seen next time */ });
        }
      })
      .catch((err) => {
        if (!alive) return;
        const gone = err instanceof ApiError && err.status === 404;
        if (!gone) trackFailed('question_load', 'load_failed');
        setFailed(gone ? 'gone' : 'load_failed');
      });
    return () => { alive = false; };
  }, [questionId]);
  if (failed === 'gone') return <PageFailure title="This question isn’t here." detail="It may have been asked of someone else, or the link is old." logState="not_found" />;
  if (failed) return <PageFailure title="Couldn’t open this." detail="Try again in a moment." logState="load_failed" />;
  if (!row) return <PageLoading />;
  const onChange = (next: Knock): void => { if (next.role === 'asker' || next.role === 'answerer') setRow(next); notifyKnocksChanged(); };
  return (
    <main className="question-page">
      <p className="question-page-kicker">{row.role === 'asker' ? 'The question Ember carried for you' : 'A question for you'}</p>
      <h1 className="question-page-head">{knockHeadline(row)}</h1>
      <QuestionCard row={row} onChange={onChange} frame="card" />
      <p className="question-page-sign">Ember</p>
    </main>
  );
}
